import { FormEvent, useEffect, useState } from 'react';
import {
  CandidateProfile,
  CvGenerationRequest,
  CvGenerationResponse,
  FocusArea,
  INDUSTRIES,
  Industry
} from '../services/cvGenerator';
import { cvDataCollection } from '../api/cv/cvDataCollectionFactory';
import type { ApiLanguage } from '../api/cv/cvDataCollection';
import { useLanguage } from '../i18n/LanguageContext';
import FilterSidepanel from './FilterSidepanel';

interface CvPageProps {
  candidateProfile: CandidateProfile | null;
  availableFocusAreas: FocusArea[];
  availableTechnologies: string[];
  dataError: string | null;
}

const toSlug = (value: string): string => {
  const slug = value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'generated-cv';
};


const getDefaultStartDate = async (language: ApiLanguage): Promise<string> => {
  if (cvDataCollection.getEarliestProjectDate) {
    return await cvDataCollection.getEarliestProjectDate(language);
  }
  // Fallback to 3 years ago if no projects data
  const date = new Date();
  date.setFullYear(date.getFullYear() - 3);
  return date.toISOString().slice(0, 10);
};

const CvPage = ({ availableFocusAreas, availableTechnologies, dataError }: CvPageProps) => {
  const { t, language } = useLanguage();
  const [candidateProfile, setCandidateProfile] = useState<CandidateProfile | null>(null);
  const [targetRole, setTargetRole] = useState('');
  const [selectedIndustries, setSelectedIndustries] = useState<Industry[]>([...INDUSTRIES]);
  const [selectedFocusAreas, setSelectedFocusAreas] = useState<FocusArea[]>([]);
  const [selectedTechnologies, setSelectedTechnologies] = useState<string[]>([]);
  const [includeCertifications, setIncludeCertifications] = useState(true);
  const [startDate, setStartDate] = useState<string>('');
  const [filterByDate, setFilterByDate] = useState(true);
  const [isGeneratingCv, setIsGeneratingCv] = useState(false);
  const [generatedCv, setGeneratedCv] = useState<CvGenerationResponse | null>(null);
  const [generateError, setGenerateError] = useState<string | null>(null);

  // Fetch profile for the current language
  useEffect(() => {
    let isMounted = true;
    cvDataCollection.getCandidateProfile(language as ApiLanguage).then((profile) => {
      if (isMounted) {
        setCandidateProfile(profile);
        setTargetRole(profile.title || 'Microsoft 365 Expert');
      }
    });
    getDefaultStartDate(language as ApiLanguage).then(setStartDate);
    return () => { isMounted = false; };
  }, [language]);

  useEffect(() => {
    setSelectedFocusAreas((current) => {
      // If already populated with valid selections, keep them
      const validSelection = current.filter((focusArea) =>
        availableFocusAreas.includes(focusArea)
      );
      if (validSelection.length > 0) {
        return validSelection;
      }
      // Otherwise, select all available focus areas
      return availableFocusAreas;
    });
  }, [availableFocusAreas]);

  useEffect(() => {
    setSelectedTechnologies((current) => {
      // If already populated with valid selections, keep them
      const validSelection = current.filter((tech) =>
        availableTechnologies.includes(tech)
      );
      if (validSelection.length > 0) {
        return validSelection;
      }
      // Otherwise, select all available technologies
      return availableTechnologies;
    });
  }, [availableTechnologies]);

  const toggleIndustry = (industry: Industry): void => {
    setSelectedIndustries((current) =>
      current.includes(industry)
        ? current.filter((entry) => entry !== industry)
        : [...current, industry]
    );
  };

  const toggleFocusArea = (focusArea: FocusArea): void => {
    setSelectedFocusAreas((current) =>
      current.includes(focusArea)
        ? current.filter((entry) => entry !== focusArea)
        : [...current, focusArea]
    );
  };

  const toggleTechnology = (technology: string): void => {
    setSelectedTechnologies((current) =>
      current.includes(technology)
        ? current.filter((entry) => entry !== technology)
        : [...current, technology]
    );
  };



  const handleGenerateCv = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    const request: CvGenerationRequest = {
      targetRole,
      industries: selectedIndustries,
      focusAreas: selectedFocusAreas,
      technologies: selectedTechnologies,
      includeCertifications,
      startDate: filterByDate ? startDate : '1970-01-01',
    };
    setIsGeneratingCv(true);
    try {
      const response = await cvDataCollection.generateCv(request, language as ApiLanguage);
      setGeneratedCv(response);
      setGenerateError(null);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : t.cv.failedToGenerate;
      setGenerateError(message);
    } finally {
      setIsGeneratingCv(false);
    }
  };

  const handleDownloadMarkdown = (): void => {
    if (!generatedCv) return;

    const file = new Blob([generatedCv.markdown], {
      type: 'text/markdown;charset=utf-8',
    });
    const url = URL.createObjectURL(file);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${toSlug(generatedCv.profile.fullName)}-cv.md`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const activeError = dataError ?? generateError;
  const allIndustriesSelected =
    INDUSTRIES.length > 0
    && INDUSTRIES.every((industry) => selectedIndustries.includes(industry));
  const allFocusAreasSelected =
    availableFocusAreas.length > 0
    && availableFocusAreas.every((focusArea) =>
      selectedFocusAreas.includes(focusArea)
    );
  const allTechnologiesSelected =
    availableTechnologies.length > 0
    && availableTechnologies.every((technology) =>
      selectedTechnologies.includes(technology)
    );
  const industriesSummary =
    selectedIndustries.length === 0 || allIndustriesSelected
      ? t.cv.all
      : selectedIndustries.join(', ');
  const focusAreasSummary =
    selectedFocusAreas.length === 0 || allFocusAreasSelected
      ? t.cv.all
      : selectedFocusAreas.join(', ');
  const technologiesSummary =
    selectedTechnologies.length === 0 || allTechnologiesSelected
      ? t.cv.all
      : selectedTechnologies.join(', ');

  return (
    <div className="cv-page-layout">
      <FilterSidepanel
        selectedIndustries={selectedIndustries}
        selectedFocusAreas={selectedFocusAreas}
        availableFocusAreas={availableFocusAreas}
        selectedTechnologies={selectedTechnologies}
        availableTechnologies={availableTechnologies}
        filterByDate={filterByDate}
        startDate={startDate}
        onToggleIndustry={toggleIndustry}
        onToggleFocusArea={toggleFocusArea}
        onToggleTechnology={toggleTechnology}
        onSetFilterByDate={setFilterByDate}
        onSetStartDate={setStartDate}
      />

      <div className="cv-page-main">
        <section className="panel">
          <h2>{t.cv.title}</h2>
          <p className="cv-intro-text">{t.cv.subtitle}</p>
          <div className="profile-banner">
            <span className="profile-label">{t.cv.candidateProfile}</span>
            <strong>{candidateProfile?.fullName || t.cv.loadingProfile}</strong>
            <span className="profile-title">
              {candidateProfile?.title || t.home.companyTagline}
            </span>
          </div>
          {activeError && <p className="api-error">{activeError}</p>}
          <form id="cv-generator-form" className="generator-form" onSubmit={handleGenerateCv}>
            <div className="form-row">
              <label htmlFor="targetRole">{t.cv.targetRole}</label>
              <input
                id="targetRole"
                value={targetRole}
                onChange={(event) => setTargetRole(event.target.value)}
              />
            </div>

            <label className="checkbox-item checkbox-inline">
              <input
                type="checkbox"
                checked={includeCertifications}
                onChange={(event) => setIncludeCertifications(event.target.checked)}
              />
              {t.cv.includeCertifications}
            </label>

          </form>

          {/* Active Filters Summary */}
          <div className="active-filters-summary">
            <h3>{t.cv.filters}:</h3>
            <div className="filters-summary-items">
              <div className="filter-summary-item">
                <span className="filter-label">{t.cv.industries}:</span>
                <span className="filter-values">{industriesSummary}</span>
              </div>
              <div className="filter-summary-item">
                <span className="filter-label">{t.cv.focusAreas}:</span>
                <span className="filter-values">{focusAreasSummary}</span>
              </div>
              {availableTechnologies.length > 0 && (
                <div className="filter-summary-item">
                  <span className="filter-label">{t.cv.technologies}:</span>
                  <span className="filter-values">{technologiesSummary}</span>
                </div>
              )}
              {filterByDate && (
                <div className="filter-summary-item">
                  <span className="filter-label">{t.cv.filterByDate}:</span>
                  <span className="filter-values">{t.cv.fromDate} {startDate}</span>
                </div>
              )}
            </div>
          </div>

          <button
            type="submit"
            form="cv-generator-form"
            className="button button-primary submit-button"
          >
            {isGeneratingCv ? t.cv.generatingButton : t.cv.generateButton}
          </button>
        </section>

        {generatedCv && (
          <section className="panel result-panel">
            <div className="result-header">
              <h2>{t.cv.generatedPreview}</h2>
              <div className="result-actions">
                <button
                  type="button"
                  className="button button-secondary"
                  onClick={handleDownloadMarkdown}
                >
                  {t.cv.downloadMarkdown}
                </button>
              </div>
            </div>
            <p className="meta">
              {t.cv.generated} {new Date(generatedCv.generatedAt).toLocaleString()}
            </p>
            <h3>{generatedCv.profile.fullName}</h3>
            <p className="headline">{generatedCv.headline}</p>
            <p>{generatedCv.summary}</p>

            <details className="skills-collapsible" open>
              <summary><h4>{t.cv.coreSkills} ({generatedCv.skills.length})</h4></summary>
              <div className="skills-compact">
                {generatedCv.skills.map((skill) => (
                  <span key={skill} className="skill-badge">{skill}</span>
                ))}
              </div>
            </details>

            <div className="project-highlights">
              <h4>{t.cv.projectHighlights}</h4>
              {generatedCv.projects.map((project) => (
                <article key={project.title} className="project-card">
                  <div className="project-card-header">
                    <strong className="project-title">{project.title}</strong>
                    <span className="project-meta-tag">{project.industry}</span>
                    <span className="project-meta-tag">
                      {new Date(project.startedAt).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })} – {project.completedAt ? new Date(project.completedAt).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }) : t.cv.ongoing}
                    </span>
                  </div>
                  <p className="project-impact">{project.impact}</p>
                  {project.focusAreas.length > 0 && (
                    <div className="project-tags">
                      {project.focusAreas.map((area) => (
                        <span key={area} className="project-tag project-tag-focus">{area}</span>
                      ))}
                    </div>
                  )}
                  {project.technologies.length > 0 && (
                    <div className="project-tags">
                      {project.technologies.map((tech) => (
                        <span key={tech} className="project-tag project-tag-tech">{tech}</span>
                      ))}
                    </div>
                  )}
                </article>
              ))}
            </div>

            <details className="markdown-output">
                  <summary>{t.cv.rawMarkdown}</summary>
              <pre>{generatedCv.markdown}</pre>
            </details>
          </section>
        )}
      </div>
    </div>
  );
};

export default CvPage;
